/**
 * Changer de période en faisant glisser le pouce.
 *
 * Les flèches restent — elles sont précises, et la seule chose qu'un lecteur d'écran sache
 * annoncer. Le glissement vient en plus : c'est le geste qu'on fait sans y penser sur un
 * téléphone, et le réclamer à chaque mois finissait par peser.
 */

/** Ce qu'un mouvement a décidé. */
export type Sens = 'precedent' | 'suivant' | null

/**
 * Décide si un déplacement du doigt est un glissement horizontal, et dans quel sens.
 *
 * Deux conditions, et les deux comptent :
 * — la distance doit dépasser un seuil, sinon chaque appui un peu traînant changerait de mois ;
 * — le mouvement doit être <b>franchement</b> horizontal, sinon un défilement vertical un peu de
 *   travers emporterait la page avec lui.
 *
 * @param dx déplacement horizontal, en pixels. Négatif vers la gauche.
 * @param dy déplacement vertical, en pixels.
 * @param seuil distance minimale pour qu'un mouvement compte.
 */
export function sensDuGlissement(dx: number, dy: number, seuil = 60): Sens {
  if (Math.abs(dx) < seuil) {
    return null
  }

  // Deux fois plus horizontal que vertical : une diagonale ne décide de rien.
  if (Math.abs(dx) < Math.abs(dy) * 2) {
    return null
  }

  // Glisser vers la gauche amène la suite, comme on tourne une page.
  return dx < 0 ? 'suivant' : 'precedent'
}

/**
 * Les gestionnaires à poser sur un élément pour qu'il réponde au glissement horizontal.
 *
 * Par événements de pointeur : ils couvrent le doigt, le stylet et la souris du même geste, là
 * où les événements tactiles laisseraient l'ordinateur de côté.
 *
 * Le défilement vertical n'est <b>pas</b> désarmé : on écoute sans empêcher, et la page continue
 * de défiler normalement sous le doigt. C'est pour cela que le seuil horizontal est franc.
 */
export function glissementHorizontal(onSens: (sens: Exclude<Sens, null>) => void) {
  let depart: { x: number; y: number } | null = null

  return {
    onPointerDown: (evenement: { clientX: number; clientY: number; pointerType: string }) => {
      // La souris ne glisse pas : elle a les flèches, et un cliquer-déplacer sur une grille
      // sélectionnerait du texte plutôt que de tourner la page.
      depart = evenement.pointerType === 'mouse' ? null : { x: evenement.clientX, y: evenement.clientY }
    },
    onPointerUp: (evenement: { clientX: number; clientY: number }) => {
      if (!depart) {
        return
      }

      const sens = sensDuGlissement(evenement.clientX - depart.x, evenement.clientY - depart.y)
      depart = null

      if (sens) {
        onSens(sens)
      }
    },
    onPointerCancel: () => {
      depart = null
    },
  }
}
